import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import { ClearFiltersIcon } from '@/components/icons'
import type { ChainRunStatus } from '@/types'
import { Search, X } from 'lucide-react'
import { chainRunStatusFilterOptions } from './chainRunStatusConfig'

interface ChainDefinitionOption {
  id: string
  displayName: string
}

interface Props {
  search: string
  status: ChainRunStatus | ''
  chainDefinitionId: string
  onSearchChange: (search: string) => void
  onStatusChange: (status: ChainRunStatus | '') => void
  onChainChange: (chainDefinitionId: string) => void
  chains: ChainDefinitionOption[]
  onClearAll: () => void
}

export function ChainRunFilters({
  search,
  status,
  chainDefinitionId,
  onSearchChange,
  onStatusChange,
  onChainChange,
  chains,
  onClearAll,
}: Props) {
  const hasActiveFilters = search !== '' || status !== '' || (chainDefinitionId !== '_all' && chainDefinitionId !== '')

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[200px] max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by chain name, user, work item"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-9 pr-9"
          aria-label="Search chain runs"
        />
        {search && (
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <Select value={status || '_all'} onValueChange={(v) => onStatusChange(v === '_all' ? '' : v as ChainRunStatus)}>
        <SelectTrigger className="w-[180px]" aria-label="Filter by status">
          <SelectValue placeholder="All Statuses" />
        </SelectTrigger>
        <SelectContent>
          {chainRunStatusFilterOptions.map(s => (
            <SelectItem key={s.value || '_all'} value={s.value || '_all'}>{s.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={chainDefinitionId || '_all'} onValueChange={onChainChange}>
        <SelectTrigger className="w-[200px]" aria-label="Filter by chain">
          <SelectValue placeholder="All Chains" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_all">All Chains</SelectItem>
          {chains.map(chain => (
            <SelectItem key={chain.id} value={chain.id}>{chain.displayName}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        type="button"
        variant="ghost"
        onClick={onClearAll}
        disabled={!hasActiveFilters}
        className="flex items-center gap-1.5 text-sm font-medium"
        aria-label="Clear all filters"
      >
        <ClearFiltersIcon className="h-4 w-4" />
        Clear All
      </Button>
    </div>
  )
}
